/**
 * The full task and note editors as a modal over whatever screen the person is on (#82).
 *
 * Until now an item could only be edited from a row already rendered in some list, which left
 * the places that *tell* you about an item — the Inbox's "Created Task: … — in Home lab", a
 * Recent Activity entry for something the watch sent in — unable to take you to it, so a task
 * the AI filed in the wrong place was found by hunting. Those places now call `openTask(id)` /
 * `openNote(id)`; one provider at the top of the app fetches the item and shows the same editor
 * its list row would, so nothing about editing is reimplemented here.
 *
 * Mounted above every card on purpose: the Inbox row that offered the Edit button may well leave
 * (its result auto-hides) while the editor is open, and an editor owned by that row would go
 * with it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteProjectQuestion } from "../deleteProject";
import {
  ApiError,
  api,
  type Area,
  type ContextEntry,
  type Note,
  type Project,
  type Task,
} from "../api";
import {
  describeRefined,
  refineContextInPlace,
  refineNoteInPlace,
  refineProjectInPlace,
  type AiRefined,
} from "../aiRefine";
import { useAreasAndProjects } from "../areasProjects";
import { EditPopupContext } from "../editPopupContext";
import { ITEM_EDITED_EVENT } from "../editPopupEvents";
import { EditContextForm } from "./EditContextForm";
import { ProjectOutlineButton } from "./ProjectOutlineButton";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { Modal } from "./Modal";
import { noteToFieldValues, destinationToIds, type NoteFieldValues } from "./noteFieldValues";
import { NoteForm } from "./NoteForm";
import { TaskEditor } from "./TaskEditor";

type Target = { kind: "task" | "note" | "project" | "context"; id: string };

const LABEL: Record<Target["kind"], string> = {
  task: "Edit task",
  note: "Edit note",
  project: "Edit project",
  context: "Edit context",
};

export function EditPopupProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = useState<Target | null>(null);
  const close = useCallback(() => setTarget(null), []);
  const value = useMemo(
    () => ({
      openTask: (id: string) => setTarget({ kind: "task", id }),
      openNote: (id: string) => setTarget({ kind: "note", id }),
      openProject: (id: string) => setTarget({ kind: "project", id }),
      openContext: (id: string) => setTarget({ kind: "context", id }),
    }),
    [],
  );

  return (
    <EditPopupContext.Provider value={value}>
      {children}
      {target && (
        <Modal label={LABEL[target.kind]} onClose={close}>
          {target.kind === "task" ? (
            <TaskPopup key={target.id} id={target.id} onClose={close} />
          ) : target.kind === "note" ? (
            <NotePopup key={target.id} id={target.id} onClose={close} />
          ) : target.kind === "project" ? (
            <ProjectPopup key={target.id} id={target.id} onClose={close} />
          ) : (
            <ContextPopup key={target.id} id={target.id} onClose={close} />
          )}
        </Modal>
      )}
    </EditPopupContext.Provider>
  );
}

function announceEdited() {
  window.dispatchEvent(new Event(ITEM_EDITED_EVENT));
}

/** Fetch one item by id; `null` while loading. A failure is shown with a way out. */
function useItem<T>(load: () => Promise<T>, onClose: () => void) {
  const [item, setItem] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    load().then(
      (result) => live && setItem(result),
      (err: unknown) => {
        if (!live) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? "That one is gone. It may have been deleted."
            : err instanceof Error
              ? err.message
              : String(err),
        );
      },
    );
    return () => {
      live = false;
    };
    // `load` closes over a stable id for the life of the popup (it is keyed on it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const failed = error !== null && (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      <p className="error" role="alert" style={{ margin: 0 }}>
        {error}
      </p>
      <div className="row wrap">
        <span style={{ flex: 1 }} />
        <button type="button" className="btn small" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
  return { item, failed };
}

function TaskPopup({ id, onClose }: { id: string; onClose: () => void }) {
  const { areas, projects, loaded } = useAreasAndProjects();
  const { item: task, failed } = useItem<Task>(() => api.task(id), onClose);
  if (failed) return failed;
  if (!task || !loaded) return <p className="muted" style={{ margin: 0 }}>Loading…</p>;

  return (
    <ul className="tasks">
      <TaskEditor
        task={task}
        areas={areas}
        projects={projects}
        onCancel={onClose}
        onSave={async (patch, recurrence) => {
          // The same two writes the task lists make for an edit; both queue when offline.
          if (Object.keys(patch).length > 0) await api.patchTask(task.id, patch);
          if (recurrence !== undefined) await api.setRecurrence(task.id, recurrence);
          announceEdited();
          onClose();
        }}
        onDelete={async (t) => {
          await api.deleteTask(t.id);
          announceEdited();
          onClose();
        }}
      />
    </ul>
  );
}

function NotePopup({ id, onClose }: { id: string; onClose: () => void }) {
  const { areas, projects, loaded } = useAreasAndProjects();
  const { item: note, failed } = useItem<Note>(() => api.note(id), onClose);
  if (failed) return failed;
  if (!note || !loaded) return <p className="muted" style={{ margin: 0 }}>Loading…</p>;
  return <NoteBody note={note} areas={areas} projects={projects} onClose={onClose} />;
}

function NoteBody({
  note,
  areas,
  projects,
  onClose,
}: {
  note: Note;
  areas: React.ComponentProps<typeof NoteForm>["areas"];
  projects: React.ComponentProps<typeof NoteForm>["projects"];
  onClose: () => void;
}) {
  const [values, setValues] = useState<NoteFieldValues>(() => noteToFieldValues(note));
  const [busy, setBusy] = useState(false);
  // Changed since the AI last answered: an answer still showing gives the buttons back.
  const [touched, setTouched] = useState(false);
  // The cards behind the popup refetch once the AI's change has been looked at, however the popup
  // ends: the answer clearing, Cancel or Escape.
  const refined = useRef(false);
  const announce = () => {
    if (!refined.current) return;
    refined.current = false;
    announceEdited();
  };
  useEffect(() => announce, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (!values.title.trim()) return;
    setBusy(true);
    try {
      // Null first so moving from an area to a project (or to neither) clears the one not named.
      await api.patchNote(note.id, {
        title: values.title.trim(),
        body: values.body || null,
        area_id: null,
        project_id: null,
        ...destinationToIds(values.destination),
      });
      announceEdited();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (
      !window.confirm(
        `Delete the note "${note.title}"?\n\n` +
          "This cannot be undone - unlike dismissing an inbox item, there is no record kept.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await api.deleteNote(note.id);
      announceEdited();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <NoteForm
      values={values}
      onChange={(patch) => {
        setTouched(true);
        setValues((current) => ({ ...current, ...patch }));
      }}
      areas={areas}
      projects={projects}
      busy={busy}
      onSubmit={save}
      onCancel={onClose}
      onDelete={remove}
      editAi={{
        onRetry: async (tier) => {
          setTouched(false);
          const answer = await refineNoteInPlace(note.id, {
            title: values.title.trim(),
            body: values.body || null,
            area_id: null,
            project_id: null,
            ...destinationToIds(values.destination),
            ...(tier ? { tier } : {}),
          });
          refined.current = true;
          setValues(noteToFieldValues(answer.item));
          return answer;
        },
        // Clearing a held answer closes the popup; a failure cleared leaves it open.
        onSettled: (result) => {
          if (!result) return;
          announce();
          onClose();
        },
        keepOpen: touched,
      }}
    />
  );
}

/** Not found among the active ones: archived, deleted, or never this account's. */
function missing(): never {
  throw new ApiError(404, "Not found");
}

/** A project the AI made: rename it, move it to another area (its tasks and notes go with it), ask
 * the AI to tidy it, or delete it if it should not have been one. Its quiet threshold and status
 * stay on the Structure page, where every project is managed. */
function ProjectPopup({ id, onClose }: { id: string; onClose: () => void }) {
  const { areas } = useAreasAndProjects();
  const { item: project, failed } = useItem<Project>(
    () => api.projects().then(({ projects }) => projects.find((p) => p.id === id) ?? missing()),
    onClose,
  );
  if (failed) return failed;
  if (!project) return <p className="muted" style={{ margin: 0 }}>Loading…</p>;
  return <ProjectBody project={project} areas={areas} onClose={onClose} />;
}

function ProjectBody({
  project,
  areas,
  onClose,
}: {
  project: Project;
  areas: Area[];
  onClose: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [areaId, setAreaId] = useState(project.area_id);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refined = useAnnounceOnClose();

  async function run(write: () => Promise<unknown>) {
    try {
      await write();
      announceEdited();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form
      style={{ display: "grid", gap: "0.6rem" }}
      data-ai-host=""
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) return;
        const patch = {
          ...(name.trim() !== project.name ? { name: name.trim() } : {}),
          ...(areaId !== project.area_id ? { area_id: areaId } : {}),
        };
        if (Object.keys(patch).length === 0) return onClose();
        void run(() => api.patchProject(project.id, patch));
      }}
    >
      {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
      <input
        className="input"
        aria-label="Project name"
        value={name}
        onChange={(e) => {
          setTouched(true);
          setName(e.target.value);
        }}
        autoFocus
      />
      <div className="row wrap">
        <select
          className="input"
          style={{ flex: "1 1 10rem", minWidth: 0 }}
          aria-label="Area"
          value={areaId}
          onChange={(e) => {
            setTouched(true);
            setAreaId(e.target.value);
          }}
        >
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      {areaId !== project.area_id && (
        <p className="small muted" style={{ margin: 0 }}>
          Its tasks and notes move with it.
        </p>
      )}
      <ProjectOutlineButton projectId={project.id} onAdded={announceEdited} />
      <div className="row wrap">
        <button
          type="button"
          className="btn ghost small danger inbox-fold"
          onClick={() => {
            if (!window.confirm(deleteProjectQuestion(project.name))) return;
            void run(() => api.deleteProject(project.id));
          }}
        >
          Delete
        </button>
        <span className="ai-spacer" style={{ flex: 1 }} />
        <button type="button" className="btn ghost small inbox-fold" onClick={onClose}>
          Cancel
        </button>
        <InboxAiRetryButton<AiRefined<Project>>
          surface="project-form"
          label="Tidy up with AI"
          busyLabel="Asking the AI"
          doneLabel="Close - the AI's changes are saved"
          disabled={!name.trim()}
          keepOpen={touched}
          describe={describeRefined}
          onRetry={async (tier) => {
            setTouched(false);
            const answer = await refineProjectInPlace(project.id, {
              name: name.trim(),
              area_id: areaId,
              ...(tier ? { tier } : {}),
            });
            refined.current = true;
            setName(answer.item.name);
            setAreaId(answer.item.area_id);
            return answer;
          }}
          onSettled={(result) => {
            if (result) onClose();
          }}
          leaveOnClear
        />
        <button className="btn primary small inbox-fold" type="submit" disabled={!name.trim()}>
          Save
        </button>
      </div>
    </form>
  );
}

/** A Context entry the AI filed: the Models page's own edit form, plus Delete and the bulb. */
function ContextPopup({ id, onClose }: { id: string; onClose: () => void }) {
  const { areas, projects } = useAreasAndProjects();
  const refined = useAnnounceOnClose();
  const { item: entry, failed } = useItem<ContextEntry>(
    () => api.context().then(({ context }) => context.find((c) => c.id === id) ?? missing()),
    onClose,
  );
  if (failed) return failed;
  if (!entry) return <p className="muted" style={{ margin: 0 }}>Loading…</p>;

  return (
    <EditContextForm
      entry={entry}
      areas={areas}
      projects={projects}
      onSave={async (values) => {
        await api.updateContext(entry.id, values);
        announceEdited();
        onClose();
      }}
      onCancel={onClose}
      onDelete={async () => {
        if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
        await api.deleteContext(entry.id);
        announceEdited();
        onClose();
      }}
      editAi={{
        onRetry: async (tier, values) => {
          const answer = await refineContextInPlace(entry.id, {
            ...values,
            ...(tier ? { tier } : {}),
          });
          refined.current = true;
          return answer;
        },
        onSettled: (result) => {
          if (result) onClose();
        },
      }}
    />
  );
}

/** Set `current` once the AI has changed the item: the cards behind refetch when the popup goes,
 * however it goes (the answer clearing, Cancel or Escape), as the note popup does. */
function useAnnounceOnClose() {
  const refined = useRef(false);
  useEffect(
    () => () => {
      if (refined.current) announceEdited();
    },
    [],
  );
  return refined;
}
